import type { ReactNode } from 'react';
import { BackLink } from './BackLink';
import { usePageLabel } from './NavTrailRecorder';
import { ParentLinks } from './ParentLinks';
import type { ParentRef } from './parentRef';
import type { UseBackOptions } from './useBack';

interface Props {
  title: string;
  /** Art des Dokuments über dem Titel, z. B. „Bericht" */
  typeLabel?: string;
  /** Titel für „Zurück zu …" auf Folgeseiten (Standard: title) */
  pageLabel?: string;
  back: UseBackOptions;
  parents?: Array<ParentRef | null | undefined>;
  /** Kennzeichen wie „ungespeichert", „verifiziert" */
  status?: ReactNode;
  /** Knöpfe rechts, z. B. <EditBar /> */
  actions?: ReactNode;
}

/**
 * Einheitlicher Dokumentkopf: Zurück, Titel, Eltern, Kennzeichen, Knöpfe (Regel
 * „Dokument-Navigation" R1 + R2). Seiten mit eigenem Kopf nutzen BackLink und ParentLinks
 * einzeln.
 */
export function DocumentHeader({ title, typeLabel, pageLabel, back, parents, status, actions }: Props) {
  usePageLabel(pageLabel ?? title, 'document');
  return (
    <header className="space-y-1" data-test-anchor="document-header">
      <div className="flex flex-wrap items-start gap-x-2 gap-y-2">
        <BackLink {...back} />
        <div className="min-w-0 pt-2">
          {typeLabel && <div className="text-xs uppercase text-slate-500">{typeLabel}</div>}
          <h1 className="text-lg sm:text-2xl font-bold text-slate-100 break-words">{title}</h1>
        </div>
        {(status || actions) && (
          <div className="flex flex-wrap items-center gap-2 pt-2 ml-auto">
            {status}
            {actions}
          </div>
        )}
      </div>
      {parents && <ParentLinks items={parents} />}
    </header>
  );
}
